import { NetworkStatus } from '@apollo/client';
import {
  Heading,
  InfoCircleIcon,
  Paragraph,
  Radio,
  SelectionInputLayout,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  ReportType,
  State,
  useGetWebcastSecurityQuery,
  useSetEventAccessPublicMutation,
} from 'src/generated/graphql-manager';
import { SetupPageFooter } from 'src/manager/components/setup-page-footer';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import { useUserPermissions } from 'src/manager/hooks/user-permissions';
import { useAbsoluteRoutes } from 'src/routes';
import {
  findAccessCodePolicy,
  findEmailRegistrationPolicy,
  findEmailWhitelistPolicy,
  findIpRangePolicy,
  findSingleSignOnPolicy,
} from 'src/utils/graphql-helpers';

import { AccessCodePolicy } from './access-code-policy';
import { EmailRegistrationPolicy } from './email-registration-policy';
import { EmailVerification } from './email-verification';
import { EmailWhitelistPolicy } from './email-whitelist-policy';
import { IpRangeProtectionPolicy } from './ip-range-protection-policy';
import { SingleSignOnPolicy } from './single-sign-on-policy';
import { DownloadReport } from '../event-summary/components/download-report/download-report';

import styles from './event-access.module.css';
import managerStyles from '../../../manager.module.css';

type EventPermissionType = 'PUBLIC' | 'RESTRICTED';

export function EventAccessPage() {
  const { t } = useTranslation();
  const { lsproId } = useCurrentUser();
  const { webcastId = '' } = useParams();
  const routes = useAbsoluteRoutes();
  const { isEventEditingAllowed } = useUserPermissions();

  const [eventPrivacy, setEventPrivacy] = useState<EventPermissionType>('PUBLIC');

  const { data, networkStatus, refetch } = useGetWebcastSecurityQuery({
    variables: { lsproId, webcastId },
    skip: !lsproId || !webcastId,
    notifyOnNetworkStatusChange: true,
  });

  const state = data?.webcast.state;
  const idps = data?.identityProviders;
  const securityPolicies = data?.webcast.securityPolicies;

  const accessCodePolicy = findAccessCodePolicy(securityPolicies);
  const singleSignOnPolicy = findSingleSignOnPolicy(securityPolicies);
  const emailWhitelistPolicy = findEmailWhitelistPolicy(securityPolicies);
  const emailRegistrationPolicy = findEmailRegistrationPolicy(securityPolicies);
  const ipRangePolicy = findIpRangePolicy(securityPolicies);

  const accessCodePolicyEnabled = accessCodePolicy?.enabled ?? false;
  const singleSignOnPolicyEnabled = singleSignOnPolicy?.enabled ?? false;
  const emailWhitelistPolicyEnabled = emailWhitelistPolicy?.enabled ?? false;
  const emailRegistrationPolicyEnabled = emailRegistrationPolicy?.enabled ?? false;

  const refetching = networkStatus === NetworkStatus.refetch;
  const formDisabled = !isEventEditingAllowed || refetching;
  const eventIsLive = state === State.LIVE || state === State.LIVE_TEST;
  const stateDisabled = !state || ![State.PRELIVE, State.LIVE, State.POSTLIVE, State.LIVE_TEST].includes(state);
  const eventPrivacyAccessDisabled = stateDisabled || (eventPrivacy === 'PUBLIC' && eventIsLive) || formDisabled;
  const ssoPolicyDisabled =
    (eventIsLive && (accessCodePolicyEnabled || emailWhitelistPolicyEnabled) && !singleSignOnPolicyEnabled) ||
    formDisabled;
  const emailWhitelistPolicyDisabled =
    (eventIsLive && (accessCodePolicyEnabled || singleSignOnPolicyEnabled) && !emailWhitelistPolicyEnabled) ||
    formDisabled;
  const accessCodePolicyDisabled =
    (eventIsLive && (singleSignOnPolicyEnabled || emailWhitelistPolicyEnabled) && !accessCodePolicyEnabled) ||
    formDisabled;

  const [setEventAccessPublicMutation, { loading: setEventAccessPublicLoading }] = useSetEventAccessPublicMutation();

  const handleSetEventPublic = () => {
    if (setEventAccessPublicLoading) return;

    setEventAccessPublicMutation({
      variables: { webcastId },
      onCompleted: (data) => {
        refetch();

        if (data.setEventAccessPublic?.__typename === 'SetEventAccessPublicSuccess') {
          setEventPrivacy('PUBLIC');
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  useEffect(() => {
    if (
      accessCodePolicy?.enabled ||
      singleSignOnPolicy?.enabled ||
      emailWhitelistPolicy?.enabled ||
      ipRangePolicy?.enabled
    )
      setEventPrivacy('RESTRICTED');
  }, [accessCodePolicy?.enabled, singleSignOnPolicy?.enabled, emailWhitelistPolicy?.enabled, ipRangePolicy?.enabled]);

  return (
    <main className={managerStyles.main} data-testid="event-access-page">
      <Heading className={managerStyles.grayText}>{t('manager.webcastSetup.eventAccess.title')}</Heading>
      <Paragraph className={managerStyles.grayText}>{t('manager.webcastSetup.eventAccess.heading')}</Paragraph>

      <div className={styles.options}>
        <Paragraph>{t('manager.webcastSetup.eventAccess.eventPrivacy.label')}</Paragraph>

        <SelectionInputLayout
          checked={eventPrivacy === 'PUBLIC'}
          disabled={eventPrivacyAccessDisabled}
          inputElement={
            <Radio
              data-testid="public-event-access-privacy"
              name="event-privacy"
              value="PUBLIC"
              checked={eventPrivacy === 'PUBLIC'}
              disabled={eventPrivacyAccessDisabled}
              onChange={handleSetEventPublic}
            >
              {t('manager.webcastSetup.eventAccess.eventPrivacy.public')}
            </Radio>
          }
        />

        <SelectionInputLayout
          checked={eventPrivacy === 'RESTRICTED'}
          disabled={eventPrivacyAccessDisabled}
          inputElement={
            <Radio
              data-testid="restricted-event-access-privacy"
              name="event-privacy"
              value="RESTRICTED"
              checked={eventPrivacy === 'RESTRICTED'}
              disabled={eventPrivacyAccessDisabled}
              onChange={() => setEventPrivacy('RESTRICTED')}
            >
              {t('manager.webcastSetup.eventAccess.eventPrivacy.restricted')}
            </Radio>
          }
        />
      </div>

      {eventPrivacy === 'RESTRICTED' && (
        <div className={styles.options} data-testid="restricted-event-access-options">
          <Paragraph>{t('manager.webcastSetup.eventAccess.accessControlMethods.label')}</Paragraph>

          {idps && idps.length > 0 && (
            <SingleSignOnPolicy
              singleSignOnPolicy={singleSignOnPolicy}
              idps={idps}
              refetch={refetch}
              formDisabled={ssoPolicyDisabled}
            />
          )}

          <EmailWhitelistPolicy
            emailWhitelistPolicy={emailWhitelistPolicy}
            refetch={refetch}
            formDisabled={emailWhitelistPolicyDisabled}
          />

          {singleSignOnPolicyEnabled && emailWhitelistPolicyEnabled && (
            <div className={styles.infoWrapper} data-testid="email-info">
              <InfoCircleIcon />
              <Trans i18nKey="manager.webcastSetup.eventAccess.infoEmails" />
            </div>
          )}

          <AccessCodePolicy
            accessCodePolicy={accessCodePolicy}
            isReadOnly={state !== State.PRELIVE}
            formDisabled={accessCodePolicyDisabled}
            refetch={refetch}
          />

          {accessCodePolicyEnabled &&
            (singleSignOnPolicyEnabled || emailWhitelistPolicyEnabled || emailRegistrationPolicyEnabled) && (
              <div className={styles.infoWrapper} data-testid="access-code-info">
                <InfoCircleIcon />
                <Trans i18nKey="manager.webcastSetup.eventAccess.infoAccessCode" />
              </div>
            )}

          <IpRangeProtectionPolicy ipRangePolicy={ipRangePolicy} formDisabled={formDisabled} refetch={refetch} />
        </div>
      )}

      <div className={styles.options}>
        <Paragraph>{t('manager.webcastSetup.eventAccess.additionalRequirements.label')}</Paragraph>

        <EmailRegistrationPolicy
          emailRegistrationPolicy={emailRegistrationPolicy}
          allowDisablingOnly={eventIsLive}
          formDisabled={state === State.ARCHIVED}
          refetch={refetch}
        />

        <EmailVerification
          isEmailWhitelistPolicyEnabled={emailWhitelistPolicyEnabled}
          requireEmailWhitelistEmailVerification={emailWhitelistPolicy?.requireEmailVerification ?? false}
          isEmailRegistrationPolicyEnabled={emailRegistrationPolicyEnabled}
          requireEmailRegistrationEmailVerification={emailRegistrationPolicy?.requireEmailVerification ?? false}
          allowDisablingOnly={eventIsLive}
          formDisabled={state === State.ARCHIVED}
          refetch={refetch}
        />
      </div>

      <div className={styles.options}>
        <Paragraph>{t('manager.webcastSetup.eventAccess.registrationList.label')}</Paragraph>

        <DownloadReport
          label={t('manager.eventSummary.reports.downloadRegisteredUsers')}
          generateLabel={t('manager.eventSummary.reports.generateRegisteredUsers')}
          infoText={t('manager.eventSummary.reports.infoIconTooltip.registeredUsersReport')}
          webcastId={webcastId}
          reportType={ReportType.REGISTERED_USERS_REPORT}
        />
      </div>

      <SetupPageFooter
        nextLabel={t('manager.webcastSetup.eventAccess.nextStep.label')}
        route={routes.webcastSetup_embedding}
      />
    </main>
  );
}
